import React, { useState, useEffect, useMemo } from "react";
import { Button, Card, CardBody, ListGroup, ListGroupItem, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input } from "reactstrap";
import { Document, Page } from "react-pdf";
import { useDispatch } from "react-redux";
import {
    getDocuments as onGetDocuments,
    getDownloadDocument as onGetDownloadDocument,
    getViewPDF as onGetViewPDF,
    updateDocument as onUpdateDocument,
    deleteDocument as onDeleteDocument
} from "../../slices/thunks";
import DocumentUploadModal from './DocumentUploadModal';

import DOCUMENT_TYPES from "./Types"

const DocumentList = ({ contract }) => {
    const dispatch = useDispatch();
    const [documents, setDocuments] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [numPages, setNumPages] = useState(null);
    const [currentDoc, setCurrentDoc] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [isViewLoading, setIsViewLoading] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [docToUpdate, setDocToUpdate] = useState(null);
    const [docToDelete, setDocToDelete] = useState(null);
    const [updateForm, setUpdateForm] = useState({
        description: '',
        document_type: ''
    });
    
    const contractId = contract?.id;
    
    const isImageFile = (fileName) => {
        const ext = fileName.toLowerCase();
        return ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.png');
    };

    const isPdfFile = (fileName) => {
        return fileName.toLowerCase().endsWith('.pdf');
    };

    useEffect(() => {
        if (contractId) {
            fetchDocuments();
        } else {
            setDocuments([]);
        }
    }, [contractId]);

    const fetchDocuments = () => {
        if (!contractId) return;
        
        dispatch(onGetDocuments({ contract: contractId }))
            .unwrap()
            .then((data) => {
                setDocuments(data);
            })
            .catch((error) => {
                console.error("Error fetching documents", error);
            });
    };

    const handleUpdateClick = (e, doc) => {
        e.stopPropagation(); // Prevent document opening
        setDocToUpdate(doc);
        setUpdateForm({
            description: doc.description || '',
            document_type: doc.document_type || ''
        });
        setUpdateModalOpen(true);
    };

    const handleDeleteClick = (e, doc) => {
        e.stopPropagation(); // Prevent document opening
        setDocToDelete(doc);
        setDeleteModalOpen(true);
    };

    const handleUpdate = async () => {
        try {
            await dispatch(onUpdateDocument({
                id: docToUpdate.id,
                description: updateForm.description,
                document_type: updateForm.document_type
            })).unwrap();
            setUpdateModalOpen(false);
            fetchDocuments();
        } catch (error) {
            console.error('Error updating document:', error);
        }
    };

    const handleDelete = async () => {
        try {
            await dispatch(onDeleteDocument(docToDelete.id)).unwrap();
            setDeleteModalOpen(false);
            fetchDocuments();
        } catch (error) {
            console.error('Error deleting document:', error);
        }
    };

    const pdfOptions = useMemo(() => ({
        cMapUrl: 'cmaps/',
        cMapPacked: true,
    }), []);

    const handleDocumentClick = async (doc) => {
        try {
            setIsViewLoading(true);
            setModalOpen(true);  // Open modal immediately for all document types
            setCurrentDoc(doc);

            if (isPdfFile(doc.file_name) || isImageFile(doc.file_name)) {
                dispatch(onGetViewPDF(doc.id))
                    .unwrap()
                    .then((blob) => {
                        const fileUrl = URL.createObjectURL(blob);
                        setSelectedDoc(fileUrl);
                        setPageNumber(1);
                    })
                    .catch((error) => {
                        console.error("Error viewing document:", error);
                    })
                    .finally(() => {
                        setIsViewLoading(false);
                    });
            } else {
                // For non-viewable files, just show the modal with download button
                setIsViewLoading(false);
            }
        } catch (error) {
            console.error('Error accessing document:', error);
            setIsViewLoading(false);
        }
    };

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (!modalOpen) {
            setSelectedDoc(null);
            setPageNumber(1);
            setNumPages(null);
            setIsViewLoading(false);  // Reset loading state when closing
        }
    };

    // Clean up object URLs when component unmounts or when selectedDoc changes
    useEffect(() => {
        return () => {
            if (selectedDoc) {
                URL.revokeObjectURL(selectedDoc);
            }
        };
    }, [selectedDoc]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    const renderDocumentViewer = () => {
        if (isViewLoading) {
            return (
                <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                    <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-3">Loading document...</div>
                </div>
            );
        }

        if (!currentDoc) return null;

        // If it's a non-viewable file type
        if (!isPdfFile(currentDoc.file_name) && !isImageFile(currentDoc.file_name)) {
            return (
                <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                    <div className="display-1 mb-4">
                        <i className="ri-file-line"></i>
                    </div>
                    <h4 className="mb-4">{currentDoc.file_name}</h4>
                    <p className="text-muted mb-4">This file type cannot be previewed in the browser.</p>
                </div>
            );
        }

        if (!selectedDoc) return null;

        if (isImageFile(currentDoc.file_name)) {
            return (
                <div className="text-center" style={{ maxHeight: 'calc(80vh - 100px)', overflow: 'auto' }}>
                    <img
                        src={selectedDoc}
                        alt={currentDoc.file_name}
                        style={{
                            maxWidth: '100%',
                            maxHeight: 'calc(80vh - 100px)',
                            objectFit: 'contain'
                        }}
                    />
                </div>
            );
        }

        if (isPdfFile(currentDoc.file_name)) {
            return (
                <div className="d-flex flex-column align-items-center">
                    <div
                        className="pdf-container text-center"
                        style={{
                            maxHeight: 'calc(80vh - 100px)',
                            overflow: 'auto',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    >
                        <Document
                            file={selectedDoc}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={
                                <div className="text-center p-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <div className="mt-2">Loading PDF...</div>
                                </div>
                            }
                            error={
                                <div className="text-center text-danger p-4">
                                    Failed to load PDF.
                                </div>
                            }
                            options={pdfOptions}
                        >
                            <Page
                                pageNumber={pageNumber}
                                loading={
                                    <div className="text-center p-2">
                                        Loading page...
                                    </div>
                                }
                                width={Math.min(window.innerWidth * 0.7, 800)}
                                className="mx-auto"
                            />
                        </Document>
                    </div>

                    {numPages && (
                        <div className="d-flex justify-content-between align-items-center mt-3 w-100 px-4">
                            <Button
                                color="primary"
                                onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                                disabled={pageNumber <= 1}
                            >
                                Previous
                            </Button>
                            <span className="mx-3">
                                Page {pageNumber} of {numPages}
                            </span>
                            <Button
                                color="primary"
                                onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))}
                                disabled={pageNumber >= numPages}
                            >
                                Next
                            </Button>
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    const handleDownload = () => {
        if (currentDoc) {
            dispatch(onGetDownloadDocument(currentDoc.id))
                .unwrap()
                .then((data) => {
                    window.open(data.download_url, '_blank');
                })
                .catch((error) => {
                    console.error("Error downloading document:", error);
                });
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'long' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };
    
    // Display empty state if no contract is provided
    if (!contractId) {
        return (
            <Card className="h-100">
                <CardBody className="d-flex flex-column justify-content-center align-items-center h-100 text-muted">
                    <i className="ri-file-list-3-line fs-2 mb-3"></i>
                    <p>Select a contract to view documents</p>
                </CardBody>
            </Card>
        );
    }

    return (
        <>
            <Card className="h-100">
                <CardBody className="d-flex flex-column h-100">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="mb-0">Documents</h5>
                        <Button
                            color="primary"
                            onClick={() => setUploadModalOpen(true)}
                            disabled={uploading || !contractId}
                        >
                            {uploading ? 'Uploading...' : 'Upload Document'}
                        </Button>
                    </div>

                    <div className="flex-grow-1 w-100" style={{overflowY: 'auto'}}>
                        {documents.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                                <i className="ri-file-list-3-line fs-3 mb-2 d-block"></i>
                                No documents found for this contract
                            </div>
                        ) : (
                            <ListGroup>
                                {documents.map((doc) => (
                                    <ListGroupItem
                                        key={doc.id}
                                        action
                                        onClick={() => handleDocumentClick(doc)}
                                        className="d-flex justify-content-between align-items-center"
                                    >
                                        <div>
                                            <div className="fw-bold">{doc.file_name}</div>
                                            <small className="text-muted">
                                                {doc.document_type}
                                            </small>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <small className="text-muted me-3">
                                                {formatDate(doc.uploaded_at)}
                                            </small>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                className="me-2"
                                                onClick={(e) => handleUpdateClick(e, doc)}
                                            >
                                                <i className="ri-edit-line"></i>
                                            </Button>
                                            <Button
                                                color="danger"
                                                size="sm"
                                                onClick={(e) => handleDeleteClick(e, doc)}
                                            >
                                                <i className="ri-delete-bin-line"></i>
                                            </Button>
                                        </div>
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        )}
                    </div>
                </CardBody>
            </Card>

            {/* Only render the DocumentUploadModal if we have a valid contractId */}
            {contractId && (
                <DocumentUploadModal
                    contract={{ id: contractId }} // Only pass the id, which is what the modal needs
                    isOpen={uploadModalOpen}
                    toggle={() => setUploadModalOpen(prev => !prev)}
                    onUploadSuccess={fetchDocuments}
                />
            )}

            {/* View pdf/image modal */}
            <Modal
                isOpen={modalOpen}
                toggle={toggleModal}
                size="lg"
                style={{ maxWidth: '90%', width: 'auto' }}
            >
                <ModalHeader toggle={toggleModal}>
                    <div>
                        <h5 className="mb-2">{currentDoc?.file_name || 'Document Viewer'}</h5>
                        <div className="d-flex" style={{ gap: '2rem' }}>
                            {currentDoc?.document_type && (
                                <div>
                                    <strong>Type:</strong>{' '}
                                    <span className="text-muted">
                                        {DOCUMENT_TYPES.find(type => type.value === currentDoc.document_type)?.label || currentDoc.document_type}
                                    </span>
                                </div>
                            )}
                            {currentDoc?.description && (
                                <div>
                                    <strong>Description:</strong>{' '}
                                    <span className="text-muted">
                                        {currentDoc.description}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="d-flex justify-content-center" style={{ minHeight: '80vh' }}>
                    {renderDocumentViewer()}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleDownload}>
                        <i className="ri-download-line me-1"></i> Download
                    </Button>
                    <Button color="secondary" onClick={toggleModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Update Document Modal */}
            <Modal isOpen={updateModalOpen} toggle={() => setUpdateModalOpen(false)}>
                <ModalHeader toggle={() => setUpdateModalOpen(false)}>
                    Update Document
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for="document_type">Document Type</Label>
                            <Input
                                type="select"
                                id="document_type"
                                value={updateForm.document_type}
                                onChange={(e) => setUpdateForm(prev => ({
                                    ...prev,
                                    document_type: e.target.value
                                }))}
                            >
                                {DOCUMENT_TYPES.map((type) => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label for="description">Description</Label>
                            <Input
                                type="textarea"
                                id="description"
                                value={updateForm.description}
                                onChange={(e) => setUpdateForm(prev => ({
                                    ...prev,
                                    description: e.target.value
                                }))}
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setUpdateModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleUpdate}>
                        Update
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)}>
                <ModalHeader toggle={() => setDeleteModalOpen(false)}>
                    Confirm Delete
                </ModalHeader>
                <ModalBody>
                    Are you sure you want to delete this document?
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button color="danger" onClick={handleDelete}>
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default DocumentList;